import '@fpsm/cxy-tokens/tokens.css';
import './oc-variables.css';

import { type Component, type FunctionComponent, useCallback, useRef } from 'react';
import { type DisclosureOptions } from 'reakit';

import { REPUBS } from '@cx-modules/constants';
import { useTranslation } from '@cx-modules/i18n-provider';
import { Accordion, Arrow, NoContent } from '@cx-modules/ui';
import { groupBy, mergePersistentParams } from '@cx-modules/utils';

import { AccordionTextWrapper } from './components/AccordionTextWrapper';
import { EventRow } from './components/EventRow';
import { GroupTitle } from './components/GroupTitle';
import { type Subevent } from './interfaces/subevent-group.interface';
import { getDateGroupKey } from './helpers';
import {
  useDynamicAlignLabelsAndBets,
  useMarketLabelsFromSubevent,
  useMatchesCouponContext,
  useSubevents,
  useTrackImpression,
} from './hooks';
import { AccordionHeader, arrow, CardWrapper, ContentWrapper, EventLink, GroupWrapper } from './MatchesCoupon.style';
import { type MatchesCouponProps } from './types';
import { findRepubInUrls, trackEvent, validateTheme } from './utils';

const RenderEventLink = ({
  eventId,
  eventName,
  requestUrl,
  strippedEventUrl,
}: {
  eventId: number;
  eventName: string;
  requestUrl: string;
  strippedEventUrl: string;
}) => {
  const { t } = useTranslation('label');
  const analyticsDataLayer = useMatchesCouponContext(state => state.analyticsDataLayer);
  const analyticsImpressionEventData = useMatchesCouponContext(state => state.analyticsImpressionEventData);

  const handleClick = useCallback(() => {
    trackEvent(
      'matches_coupon_see_all_games_click',
      { eventId, eventName, ...analyticsImpressionEventData },
      analyticsDataLayer,
    );
  }, [analyticsDataLayer, analyticsImpressionEventData, eventId, eventName]);

  return (
    <EventLink
      data-testid="all-games-link"
      href={mergePersistentParams(strippedEventUrl, requestUrl)}
      onClick={handleClick}
    >
      <span>{t('CX_MATCHES_COUPON_SEE_ALL_GAMES', { ns: 'label' })}</span>
      <Arrow colour="var(--ogm-timberwolves)" right size={3} x={1} y={-1} />
    </EventLink>
  );
};

export const MatchesCoupon: FunctionComponent<MatchesCouponProps> = ({
  baseUrl,
  eventId,
  eventName,
  eventUrl,
  repub,
  requestUrl,
  showEventCta,
  theme,
}) => {
  const validatedTheme = validateTheme(theme || 'oddschecker');
  const isWidget = validatedTheme === 'widget';
  const subevents = useSubevents();
  const noSubeventsOrMatches = subevents.length === 0 || subevents.filter(s => s.type === 'MATCH').length === 0;
  const accordionRef = useRef<Component<'button', DisclosureOptions, any>>(null);
  const timeZone = useMatchesCouponContext(state => state.timeZone) ?? REPUBS[repub].dateProperties.timezone;
  const analyticsDataLayer = useMatchesCouponContext(state => state.analyticsDataLayer);
  const analyticsImpressionEventData = useMatchesCouponContext(state => state.analyticsImpressionEventData);
  const analyticsImpressionEventName = useMatchesCouponContext(state => state.analyticsImpressionEventName);

  const impressionRef = useTrackImpression(
    analyticsImpressionEventName,
    analyticsImpressionEventData,
    analyticsDataLayer,
  );

  const handleAccordionToggle = useCallback(
    (visible: boolean) => {
      trackEvent(
        visible ? 'matches_coupon_accordion_expand' : 'matches_coupon_accordion_collapse',
        { eventId, eventName, ...analyticsImpressionEventData },
        analyticsDataLayer,
      );
    },
    [analyticsDataLayer, analyticsImpressionEventData, eventId, eventName],
  );
  const groupedSubevents = Object.entries(
    groupBy(
      subevents.filter(s => Boolean(s.startTime) && s.type === 'MATCH'),
      (subevent: Subevent) => {
        return getDateGroupKey(subevent.startTime, timeZone);
      },
    ),
  )
    .sort(([dateKeyA], [dateKeyB]) => dateKeyA.localeCompare(dateKeyB))
    .map(([, matches]): [string, Subevent[]] => [
      matches[0].startTime,
      matches.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()),
    ]);
  const strippedEventUrl = findRepubInUrls(baseUrl, eventUrl, repub) || '';
  const categoryGroupId = subevents[0]?.categoryGroupId;
  const marketLabels = useMarketLabelsFromSubevent();
  const renderBadge = useMatchesCouponContext(state => state.renderOddsButtonBadge);
  const showEventHeader = useMatchesCouponContext(state => state.showEventHeader);
  const showMoreOddsButton = useMatchesCouponContext(state => state.showMoreOddsButton);
  const showTeamLogos = useMatchesCouponContext(state => state.showTeamLogos);

  const { groupTitleLabelsMarginRight, setBetsWrapperWidth, setGroupTitleLabelsMarginRight } =
    useDynamicAlignLabelsAndBets();

  const renderNoContent = () => {
    if (noSubeventsOrMatches) return <NoContent>No matches available at the moment. Check again later.</NoContent>;
  };

  const couponContent = (
    <>
      <ContentWrapper>
        {renderNoContent()}
        {groupedSubevents.map(([dateString, subevents]) => {
          return (
            <GroupWrapper
              data-testid="matches-group"
              key={`${eventId}-${dateString}`}
              numberOfColumns={marketLabels.length}
              renderBadge={renderBadge}
            >
              <GroupTitle
                dateIsoString={subevents?.[0]?.startTime}
                marketLabels={marketLabels}
                repub={repub}
                setBetsWrapperWidth={setBetsWrapperWidth}
                tabletUpMarginRight={showMoreOddsButton ? groupTitleLabelsMarginRight : 0}
              />
              {subevents.map(subevent => {
                const hasHomeTeamBadge = !!subevent.homeTeam?.badgeUrl;
                const hasAwayTeamBadge = !!subevent.awayTeam?.badgeUrl;
                const showTeamBadges = showTeamLogos ?? (hasHomeTeamBadge || hasAwayTeamBadge);
                if (subevent.type === 'MATCH' && subevent.homeTeam && subevent.awayTeam) {
                  return (
                    <EventRow
                      key={subevent.id}
                      repub={repub}
                      requestUrl={requestUrl}
                      setGroupTitleLabelsMarginRight={setGroupTitleLabelsMarginRight}
                      showBottomBorder={true}
                      showTeamBadges={showTeamBadges}
                      subevent={subevent}
                    />
                  );
                } else {
                  return null;
                }
              })}
            </GroupWrapper>
          );
        })}
      </ContentWrapper>

      {showEventCta ? (
        <RenderEventLink
          eventId={eventId}
          eventName={eventName}
          requestUrl={requestUrl}
          strippedEventUrl={strippedEventUrl}
        />
      ) : null}
    </>
  );

  const renderHeader = () => {
    if (!showEventHeader) return couponContent;

    if (isWidget) {
      return (
        <>
          <AccordionHeader data-testid={`${eventName}-matches`}>
            <AccordionTextWrapper
              categoryGroupId={categoryGroupId}
              interactive={false}
              label={eventName}
              requestUrl={requestUrl}
              url={strippedEventUrl}
            />
          </AccordionHeader>
          {couponContent}
        </>
      );
    }

    return (
      <Accordion
        header={
          <AccordionHeader className={arrow} data-testid={`${eventName}-matches`}>
            <AccordionTextWrapper
              categoryGroupId={categoryGroupId}
              interactive={false}
              label={eventName}
              requestUrl={requestUrl}
              url={strippedEventUrl}
            />
          </AccordionHeader>
        }
        id={`accordion-${eventId}`}
        onToggle={handleAccordionToggle}
        ref={accordionRef}
        visible={true}
      >
        {couponContent}
      </Accordion>
    );
  };

  return (
    <CardWrapper
      className={validatedTheme}
      data-fpsm-theme={validatedTheme}
      data-testid="matches-coupon"
      id={`event_${eventId}`}
      ref={impressionRef}
    >
      {renderHeader()}
    </CardWrapper>
  );
};
